import React from 'react';
import { RouteComponentProps } from 'react-router';
import { userRolesToLabelMap } from '@bb/users';
import { LayoutContext, AuthContext } from 'contexts';
import { useBreakpoint, useTableResizer } from 'hooks';
import { save } from 'api/GroupController';
import errorReporter from '@app/errorReporter';
import {
  Popconfirm,
  Avatar,
  Button,
  Dropdown,
  Icon,
  Menu,
  MenuItem,
  Space,
  TableProps,
  message,
  Text,
} from 'components';

import { useHistory } from 'react-router-dom';
import { StyledTable, UserStatusTag } from '../../../components';
import ManageUsersContext from '../ManageUsersContext';
import ManageUsersPagination from './ManageUsersPagination';
import { addDatacy, addDatacyToHeader, GroupMember } from '../util';
import { TeamGroupModal } from '../TeamGroupModal';

export interface MatchParams {
  groupId?: string;
}

const GroupsMembersTable: React.FC<RouteComponentProps<MatchParams>> = ({
  match,
}) => {
  const groupId = match.params.groupId || '';
  const { transition, setAppTitle } = React.useContext(LayoutContext);
  const { auth } = React.useContext(AuthContext);
  const {
    isGroupMemberLoading: isLoading,
    selectedGroupInfo,
    selectedGroupUnfilteredUsers,
    setBreadcrumbRoutes,
    setGroupId,
    fetchGroupMembers,
    setSearchText,
  } = React.useContext(ManageUsersContext);

  const [popconfirmVisibility, setPopconfirmVisibility] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const { md } = useBreakpoint();
  const history = useHistory();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tableWrapperRef = React.useRef<HTMLDivElement>();

  const { tableHeight, tableHeaderHeight } = useTableResizer({
    wrapperRef: tableWrapperRef,
  });

  React.useEffect(() => {
    setGroupId(groupId);
    setSearchText('');
    return () => {
      setSearchText('');
    };
  }, [groupId, setGroupId, setSearchText]);

  React.useEffect(() => {
    if (selectedGroupInfo?.name) {
      setAppTitle(selectedGroupInfo.name);
      setBreadcrumbRoutes([
        {
          breadcrumbName: 'Groups',
          path: '/users/list?tabKey=groups',
        },
        {
          breadcrumbName: selectedGroupInfo.name,
          path: `/users/list/group/${selectedGroupInfo.id}`,
        },
      ]);
    }
  }, [
    selectedGroupInfo?.id,
    selectedGroupInfo?.name,
    setAppTitle,
    setBreadcrumbRoutes,
  ]);

  React.useEffect(() => () => setGroupId(''), [setGroupId]);

  const columns: TableProps<GroupMember>['columns'] = [
    {
      width: 250,
      title: 'Name',
      key: 'name',
      dataIndex: '',
      render: (_, user) => (
        <Space align="center" size="middle">
          <Avatar user={user} />
          <Text>{`${user.firstName} ${user.lastName}`}</Text>
        </Space>
      ),
      onCell: addDatacy('users-name'),
      onHeaderCell: addDatacyToHeader('users-name'),
    },
    {
      width: 80,
      title: 'Type',
      key: 'type',
      dataIndex: 'role',
      align: 'center',
      onCell: addDatacy('users-type'),
      onHeaderCell: addDatacyToHeader('users-type'),
    },
    {
      width: 100,
      title: 'Status',
      key: 'status',
      dataIndex: '',
      align: 'center',
      render: (_, { disabled, isUnverified, isDeleted }) => (
        <UserStatusTag
          disabled={disabled}
          isUnverified={isUnverified}
          isDeleted={isDeleted}
        />
      ),
      onCell: addDatacy('users-status'),
      onHeaderCell: addDatacyToHeader('users-status'),
    },
    {
      width: 200,
      title: 'Email',
      key: 'email',
      dataIndex: 'email',
      onCell: addDatacy('users-email'),
      onHeaderCell: addDatacyToHeader('users-email'),
    },
    {
      width: 125,
      title: 'Branch',
      key: 'location',
      dataIndex: 'location',
      onCell: addDatacy('users-branch'),
      onHeaderCell: addDatacyToHeader('users-branch'),
    },
    {
      width: 300,
      title: 'Address',
      key: 'address',
      dataIndex: 'address',
      onCell: addDatacy('users-address'),
      onHeaderCell: addDatacyToHeader('users-address'),
    },
    {
      width: 80,
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      align: 'center',
      render: (_, user: GroupMember) => (
        <Dropdown
          placement="topLeft"
          trigger={['click']}
          getPopupContainer={node => node.parentElement}
          dropdownRender={() => (
            <div onClick={e => e.stopPropagation()}>
              <Popconfirm
                title="Are you sure you’d like to remove this user from the group?"
                width={md ? undefined : '85vw'}
                open={popconfirmVisibility}
                onCancel={() => setPopconfirmVisibility(false)}
                onConfirm={async () => {
                  try {
                    // Use unfiltered users so as not to delete filtered ones
                    const newUsers = selectedGroupUnfilteredUsers
                      .map(({ id }) => id)
                      .filter(id => id !== user.id);
                    setGroupId('');
                    setIsDeleting(true);
                    await save({ ...selectedGroupInfo, users: newUsers });
                  } catch (err) {
                    message.error('Error removing user from group');
                    errorReporter.error(err);
                  } finally {
                    setGroupId(groupId);
                    setIsDeleting(false);
                  }
                }}
                okText=">  Yes"
                cancelText="> No"
                placement="topRight"
                data-cy="users-options-dropdown"
              >
                <div>
                  <Menu>
                    <MenuItem
                      key={'remove'}
                      onClickCapture={e => {
                        setPopconfirmVisibility(true);
                        e.stopPropagation();
                      }}
                      data-cy="users-options-remove"
                    >
                      Remove from Group
                    </MenuItem>
                  </Menu>
                </div>
              </Popconfirm>
            </div>
          )}
        >
          <Button type="text" size="small" onClick={e => e.stopPropagation()}>
            <Icon type="ellipsis" width={25} />
          </Button>
        </Dropdown>
      ),
      onCell: addDatacy('users-options'),
    },
  ];
  const processedColumns = columns.filter(Boolean);

  const dataSource =
    auth &&
    selectedGroupInfo?.users?.map(
      ({
        id,
        avatarFilestackId,
        firstName,
        lastName,
        email,
        location,
        address,
        role,
        disabled,
        isUnverified,
        isDeleted,
      }) => ({
        id,
        key: id,
        firstName,
        lastName,
        email,
        avatarFilestackId,
        disabled,
        isUnverified,
        isDeleted,
        role: userRolesToLabelMap[role],
        location: auth?.org?.locations?.[Number(location)]?.name,
        address:
          auth?.org?.addresses?.[Number(location)]?.[Number(address)].address,
      }),
    );

  return (
    <>
      <div ref={tableWrapperRef}>
        <StyledTable
          style={{ transition: `all ${transition}s` }}
          tableLayout="fixed"
          pagination={false}
          $height={tableHeight}
          $headerHeight={tableHeaderHeight}
          showSorterTooltip={false}
          loading={isLoading || isDeleting}
          dataSource={dataSource}
          columns={processedColumns}
          scroll={{
            x: '100%',
            y: `calc(100% - ${tableHeaderHeight}px)`,
          }}
          onRow={user => ({
            onClick: () => history.push(`/users/edit/${user.id}`),
          })}
        />
        <ManageUsersPagination hidden />
      </div>
      <TeamGroupModal destroyOnClose onSave={() => fetchGroupMembers(true)} />
    </>
  );
};

export default GroupsMembersTable;
